import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contactApi } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

function Ic({ type, size = 20, color = 'var(--soleil-gold)' }: { type: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const icons: Record<string, React.ReactNode> = {
    mail: <svg {...p}><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22 4 12 13 2 4" /></svg>,
    phone: <svg {...p}><path d="M22 16.92v3a2 2 0 01-2.18 2A19.86 19.86 0 013.09 5.18 2 2 0 015.11 3h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 11.91a16 16 0 006 6l2.27-2.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>,
    pin: <svg {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  };
  return <>{icons[type] || null}</>;
}

function IcBox({ type, color, bg, size = 44 }: { type: string; color: string; bg: string; size?: number }) {
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.26, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Ic type={type} size={size * 0.48} color={color} />
    </div>
  );
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '13px 15px', background: 'var(--soleil-cream)',
  border: '1.5px solid var(--soleil-sand)', borderRadius: 11,
  color: 'var(--soleil-ink)', fontFamily: S.font, fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

export default function SoleilContactPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) => setTouched(prev => ({ ...prev, [field]: true }));

  const subjectError = touched.subject && formData.subject.length > 0 && formData.subject.length < 2;
  const messageError = touched.message && formData.message.length > 0 && formData.message.length < 15;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.subject.length < 2) {
      toast({ title: 'Error', description: 'Subject must be at least 2 characters', variant: 'destructive' });
      return;
    }
    if (formData.message.length < 15) {
      toast({ title: 'Error', description: 'Message must be at least 15 characters', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      await contactApi.submit(formData);
      toast({ title: 'Success', description: 'Message sent successfully!' });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to send message', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section style={{ minHeight: '80vh', background: 'var(--soleil-cream)', padding: '56px 24px 72px' }}>
      <div className="soleil-grid-contact" style={{ maxWidth: 940, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 52 }}>
        {/* Left — info */}
        <div>
          <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-sienna)', letterSpacing: '0.2em', marginBottom: 14 }}>
            {isRTL ? 'تواصل معنا' : 'CONTACT'}
          </div>
          <h1 style={{ fontFamily: S.heading, fontSize: 38, fontWeight: 600, color: 'var(--soleil-ink)', lineHeight: 1.15 }}>
            {isRTL ? 'لنتواصل ' : "Let's "}
            <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
              {isRTL ? 'معاً' : 'connect'}
            </span>
          </h1>
          <p style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-sub)', lineHeight: 1.75, marginTop: 14, marginBottom: 36 }}>
            {t('contact.subtitle')}
          </p>
          {([
            ['mail', 'support@itilshift.com', isRTL ? 'البريد الإلكتروني' : 'Email', 'var(--soleil-gold)', 'var(--soleil-gold-pale)'],
            ['phone', '+964 788 446 6494', isRTL ? 'الهاتف' : 'Phone', 'var(--soleil-sienna)', 'var(--soleil-sienna-pale)'],
            ['pin', isRTL ? 'بغداد، العراق' : 'Baghdad, Iraq', isRTL ? 'الموقع' : 'Location', 'var(--soleil-olive)', 'var(--soleil-olive-pale)'],
          ] as [string, string, string, string, string][]).map(([ic, val, label, cl, bg], i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10, padding: '16px 18px', background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 14 }}>
              <IcBox type={ic} color={cl} bg={bg} />
              <div>
                <div style={{ fontFamily: S.font, fontSize: 10, color: 'var(--soleil-mut)', fontWeight: 700, letterSpacing: '0.08em' }}>{label.toUpperCase()}</div>
                <div style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-ink)', fontWeight: 600, marginTop: 1 }}>{ic === 'phone' ? <div><div dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{val}</div><div style={{ display: 'flex', gap: 8, marginTop: 6 }}><a href={'tel:' + val.replace(/\s/g, '')} style={{ color: 'var(--soleil-sienna)', textDecoration: 'none', fontSize: 11, padding: '3px 10px', border: '1px solid var(--soleil-sand)', borderRadius: 20, fontWeight: 600, letterSpacing: '0.02em' }}>📞 Call</a><a href={'https://wa.me/' + val.replace(/[\s+]/g, '')} target='_blank' rel='noopener noreferrer' style={{ color: '#25D366', textDecoration: 'none', fontSize: 11, padding: '3px 10px', border: '1px solid #d4edda', borderRadius: 20, fontWeight: 600, letterSpacing: '0.02em' }}>💬 WhatsApp</a></div></div> : ic === 'mail' ? <a href={'mailto:' + val} style={{ color: 'inherit', textDecoration: 'none' }}>{val}</a> : val}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Right — form */}
        <div style={{ background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 20, padding: 36, boxShadow: '0 4px 20px rgba(46,42,38,0.04)' }}>
          <form onSubmit={handleSubmit}>
            <div className="soleil-grid-form" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {([
                [t('contact.form.name'), 'name', 'text', true, ''],
                [t('contact.form.email'), 'email', 'email', true, ''],
                [t('contact.form.phone'), 'phone', 'text', false, ''],
                [t('contact.form.subject'), 'subject', 'text', true, isRTL ? 'حرفان على الأقل' : 'Min. 2 characters'],
              ] as [string, string, string, boolean, string][]).map(([label, key, type, req, hint]) => {
                const isErr = key === 'subject' && subjectError;
                return (
                <div key={key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
                    <label style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: 'var(--soleil-sub)' }}>{label}</label>
                    {hint && <span style={{ fontFamily: S.font, fontSize: 11, color: isErr ? '#C0392B' : 'var(--soleil-mut)', transition: 'color 0.2s' }}>{isErr ? (isRTL ? 'قصير جداً' : 'Too short') : hint}</span>}
                  </div>
                  <input
                    type={type}
                    placeholder={label}
                    value={(formData as any)[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    onBlur={() => markTouched(key)}
                    required={req}
                    style={{
                      ...inputStyle,
                      borderColor: isErr ? '#C0392B' : 'var(--soleil-sand)',
                      transition: 'border-color 0.2s',
                    }}
                  />
                </div>
                );
              })}
            </div>
            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
                <label style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: 'var(--soleil-sub)' }}>
                  {t('contact.form.message')}
                </label>
                <span style={{ fontFamily: S.font, fontSize: 11, color: messageError ? '#C0392B' : 'var(--soleil-mut)', transition: 'color 0.2s' }}>
                  {messageError
                    ? (isRTL ? `${15 - formData.message.length} أحرف متبقية` : `${15 - formData.message.length} more characters needed`)
                    : (isRTL ? '15 حرف على الأقل' : 'Min. 15 characters')
                  }
                </span>
              </div>
              <textarea
                placeholder={isRTL ? 'كيف يمكننا مساعدتك؟' : 'How can we help?'}
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                onBlur={() => markTouched('message')}
                required
                style={{
                  ...inputStyle,
                  resize: 'vertical' as const,
                  borderColor: messageError ? '#C0392B' : 'var(--soleil-sand)',
                  transition: 'border-color 0.2s',
                }}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%', marginTop: 20, padding: 16,
                fontFamily: S.font, fontSize: 15, fontWeight: 600, color: 'var(--soleil-dark)',
                background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
                border: 'none', borderRadius: 11, cursor: isLoading ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 16px rgba(184,148,58,0.2)',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {isLoading ? (isRTL ? 'جاري الإرسال...' : 'Sending...') : (isRTL ? 'إرسال الرسالة ←' : 'Send message →')}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
